/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState } from 'react'
import { FashionProductionOrderData } from '../pages/ClientPanel/ProductionFashion/FashionPcp'
import { UserStatus } from '../types/userStatus'

type StockMode = 'normal' | 'fashion'

export interface UserAttachment {
  id: number
  fileUrl: string
  title: string
}

export type SkillOption = { id: number; name: string }

/** Usuário da tabela de acessos selecionado para edição (tenant admin) */
export interface AccessUserForEdit {
  id: number
  name: string
  email: string
  phone: string
  company: string
  status: UserStatus
  cnpj: string
  adminUser: boolean
  permissions: string[]
  approved: string
  profileId: number | null
  attachments?: UserAttachment[]
  skills?: SkillOption[]
}

/** Perfil selecionado para edição do nome (tenant admin) */
export interface EditingProfile {
  id: number
  name: string
}

interface ModalContextProps {
  modalActive: boolean
  setModalActive: React.Dispatch<React.SetStateAction<boolean>>
  modalSection: string
  setModalSection: React.Dispatch<React.SetStateAction<string>>
  itIsUpdate: boolean
  setItIsUpdate: React.Dispatch<React.SetStateAction<boolean>>
  lastSection:string
  setLastSection:React.Dispatch<React.SetStateAction<string>>
  selectedProductionOrder: any | null; // Armazena a OP selecionada
  setSelectedProductionOrder: (order: any | null) => void; // Função para definir a OP
  selectedFashionOrderId: number | null
  setSelectedFashionOrderId: (id: number | null) => void
  selectedCompany: any | null; // Armazena a empresa selecionada para edição
  setSelectedCompany: (company: any | null) => void; // Função para definir a empresa
  selectedSteelOrder: any | null;
  setSelectedSteelOrder: (order: any | null) => void;
  selectedTenant: any | null;
  setSelectedTenant: (tenant: any | null) => void;
  selectedAccessUser: AccessUserForEdit | null;
  setSelectedAccessUser: (user: AccessUserForEdit | null) => void;
  editingProfile: EditingProfile | null;
  setEditingProfile: (profile: EditingProfile | null) => void;
  refreshCallback: (() => void) | null;
  setRefreshCallback: (callback: (() => void) | null) => void;
  stockMode: StockMode;
  setStockMode: React.Dispatch<React.SetStateAction<StockMode>>;
  isVariation: boolean;
  setIsVariation: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModalContext = createContext<ModalContextProps>({
  modalActive: false,
  setModalActive: () => {},
  modalSection: '',
  setModalSection: () => {},
  itIsUpdate: false,
  setItIsUpdate: () => {},
  lastSection:'',
  setLastSection:() => {},
  selectedProductionOrder: null,
  setSelectedProductionOrder: () => {},
  selectedFashionOrderId: null,
  setSelectedFashionOrderId: () => {},
  selectedCompany: null,
  setSelectedCompany: () => {},
  selectedSteelOrder: null,
  setSelectedSteelOrder: () => {},
  selectedTenant: null,
  setSelectedTenant: () => {},
  selectedAccessUser: null,
  setSelectedAccessUser: () => {},
  editingProfile: null,
  setEditingProfile: () => {},
  refreshCallback: null,
  setRefreshCallback: () => {},
  stockMode: 'normal',
  setStockMode: () => {},
  isVariation: false,
  setIsVariation: () => {},
})

export default ModalContext

export const ModalProvider: React.FC<any> = ({ children }) => {
  const [ modalActive, setModalActive ] = useState<boolean>(false)
  const [ modalSection, setModalSection ] = useState<string>('stock')
  const [ itIsUpdate, setItIsUpdate ] = useState<boolean>(false)
  const [ lastSection, setLastSection ] = useState<string>('')
  const [selectedProductionOrder, setSelectedProductionOrder] = useState<any | null>(null);
  const [selectedFashionOrderId, setSelectedFashionOrderId] = useState<number | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [selectedSteelOrder, setSelectedSteelOrder] = useState<any | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
  const [selectedAccessUser, setSelectedAccessUser] = useState<AccessUserForEdit | null>(null);
  const [editingProfile, setEditingProfile] = useState<EditingProfile | null>(null);
  const [refreshCallback, setRefreshCallback] = useState<(() => void) | null>(null);
  const [stockMode, setStockMode] = useState<StockMode>('normal');
  const [isVariation, setIsVariation] = useState<boolean>(false);
  

  const setModalSectionWithLogs = (value: React.SetStateAction<string>) => {
    const newSection = typeof value === 'function' ? value(modalSection) : value;
    
    console.log('🔄 [MODAL-CONTEXT] Mudando seção do modal:', {
      from: modalSection,
      to: newSection,
      timestamp: new Date().toISOString(),
      modalActive
    });
    
    setLastSection(modalSection);
    setModalSection(value);
    
    console.log('✅ [MODAL-CONTEXT] Seção alterada com sucesso:', {
      currentSection: newSection,
      previousSection: modalSection,
      isActive: modalActive
    });
  };
  

  const setModalActiveWithLogs = (value: React.SetStateAction<boolean>) => {
    const newActive = typeof value === 'function' ? value(modalActive) : value;
    
    console.log('🔄 [MODAL-CONTEXT] Mudando estado do modal:', {
      from: modalActive,
      to: newActive,
      currentSection: modalSection,
      timestamp: new Date().toISOString()
    });
    
    setModalActive(value);
    
    if (!newActive) {
      console.log('💫 [MODAL-CONTEXT] Modal fechado - limpando contexto');
      setStockMode('normal');
      setIsVariation(false);
      setSelectedFashionOrderId(null)
      setSelectedAccessUser(null)
      setEditingProfile(null)
    } else {
      console.log('✅ [MODAL-CONTEXT] Modal aberto na seção:', modalSection);
    }
  }; 

  return (
    <ModalContext.Provider
      value={{
        modalActive,
        setModalActive: setModalActiveWithLogs,
        modalSection,
        setModalSection: setModalSectionWithLogs,
        itIsUpdate,
        setItIsUpdate,
        lastSection,
        setLastSection,
        selectedProductionOrder,
        setSelectedProductionOrder,
        selectedFashionOrderId,
        setSelectedFashionOrderId,
        selectedCompany,
        setSelectedCompany,
        selectedSteelOrder,
        setSelectedSteelOrder,
        selectedTenant,
        setSelectedTenant,
        selectedAccessUser,
        setSelectedAccessUser,
        editingProfile,
        setEditingProfile,
        refreshCallback,
        setRefreshCallback,
        stockMode,
        setStockMode,
        isVariation,
        setIsVariation,
      }}
    >
      {children}
    </ModalContext.Provider>
  )
}
